/**
 * Payor validation provider abstraction (US-028).
 *
 * The Payor model carries `validationStatus` / `validationProvider` /
 * `validationDetails` columns. This package owns the interface every adapter
 * implements + the mock/middesk adapters themselves. The router calls
 * `getProvider()` once per request and never names a concrete adapter; the
 * provider is selected by the `PAYOR_VALIDATION_PROVIDER` env var.
 *
 * Adapters MUST be:
 *   - Non-blocking: a failed validation never throws back to the router.
 *     Errors collapse to `unable_to_verify` with details so the UI can still
 *     render a useful badge.
 *   - Deterministic in test/mock modes — no random network calls.
 *
 * @package @app/payor-validation
 */

export type PayorValidationStatus =
  | 'unverified'
  | 'verified'
  | 'unable_to_verify'
  | 'flagged';

export interface PayorValidationInput {
  legalName: string;
  ein?: string | null;
  website?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

export interface PayorValidationResult {
  status: PayorValidationStatus;
  provider: string;
  /**
   * Free-form structured details (will be JSON-stringified for storage).
   * Adapters should populate at least:
   *   - `nameMatch`: 'exact' | 'fuzzy' | 'none' | null
   *   - `einMatch`: boolean | null
   *   - `registrySource`: string | null
   *   - `watchlistHits`: string[] (empty array if none)
   *   - `reason`: human-readable summary
   */
  details: {
    nameMatch?: 'exact' | 'fuzzy' | 'none' | null;
    einMatch?: boolean | null;
    registrySource?: string | null;
    watchlistHits?: string[];
    reason?: string;
    [key: string]: unknown;
  };
}

export interface PayorValidationProvider {
  readonly name: string;
  validate(input: PayorValidationInput): Promise<PayorValidationResult>;
}

// ---------------------------------------------------------------------------
// Mock adapter — the default. Returns `unable_to_verify` cleanly so local dev
// and CI run with zero secrets. Deterministic short-circuits in the input let
// tests force a status:
//   - legalName containing "[verified]"  → status=verified
//   - legalName containing "[flagged]"   → status=flagged with watchlist hit
//   - legalName containing "[fail]"      → simulates a thrown adapter error,
//     which the safe-call wrapper still collapses to `unable_to_verify`
// ---------------------------------------------------------------------------
export class MockPayorValidationProvider implements PayorValidationProvider {
  readonly name = 'mock';

  async validate(input: PayorValidationInput): Promise<PayorValidationResult> {
    const tag = input.legalName.toLowerCase();
    if (tag.includes('[fail]')) {
      throw new Error('Mock: simulated adapter failure');
    }
    if (tag.includes('[flagged]')) {
      return {
        status: 'flagged',
        provider: this.name,
        details: {
          nameMatch: 'exact',
          einMatch: input.ein ? true : null,
          registrySource: 'mock',
          watchlistHits: ['OFAC SDN (mock)'],
          reason: 'Mock match against simulated watchlist',
        },
      };
    }
    if (tag.includes('[verified]')) {
      return {
        status: 'verified',
        provider: this.name,
        details: {
          nameMatch: 'exact',
          einMatch: input.ein ? true : null,
          registrySource: 'mock',
          watchlistHits: [],
          reason: 'Mock verified',
        },
      };
    }
    return {
      status: 'unable_to_verify',
      provider: this.name,
      details: {
        nameMatch: null,
        einMatch: null,
        registrySource: null,
        watchlistHits: [],
        reason: 'No production validation provider configured (PAYOR_VALIDATION_PROVIDER=mock).',
      },
    };
  }
}

// ---------------------------------------------------------------------------
// Middesk adapter — placeholder. The shape is wired so the env-flag flip
// works the moment we drop in real credentials and the SDK call. We keep
// it failing to `unable_to_verify` until then so production never sees a
// silent fake-verified result.
// ---------------------------------------------------------------------------
export class MiddeskPayorValidationProvider implements PayorValidationProvider {
  readonly name = 'middesk';

  constructor(private readonly apiKey: string) {
    if (!apiKey) {
      throw new Error('MIDDESK_API_KEY required for MiddeskPayorValidationProvider');
    }
  }

  async validate(input: PayorValidationInput): Promise<PayorValidationResult> {
    // TODO: real Middesk API integration (POST /v1/businesses) and IRS TIN
    // Match. Tracked under US-028 follow-up — until then we return a clean
    // "unable_to_verify" so the badge is honest in production environments
    // that haven't completed the integration.
    return {
      status: 'unable_to_verify',
      provider: this.name,
      details: {
        nameMatch: null,
        einMatch: null,
        registrySource: 'middesk',
        watchlistHits: [],
        reason: 'Middesk adapter present but not yet wired to API (US-028 follow-up).',
      },
    };
  }
}

// ---------------------------------------------------------------------------
// Provider selection. Single entry point used by the router.
// ---------------------------------------------------------------------------
let cachedProvider: PayorValidationProvider | null = null;

export function getPayorValidationProvider(): PayorValidationProvider {
  if (cachedProvider) return cachedProvider;
  const choice = (process.env.PAYOR_VALIDATION_PROVIDER ?? 'mock').toLowerCase();
  if (choice === 'middesk') {
    const apiKey = process.env.MIDDESK_API_KEY ?? '';
    if (!apiKey) {
      console.warn('[payor-validation] MIDDESK_API_KEY missing — falling back to mock');
      cachedProvider = new MockPayorValidationProvider();
      return cachedProvider;
    }
    cachedProvider = new MiddeskPayorValidationProvider(apiKey);
    return cachedProvider;
  }
  cachedProvider = new MockPayorValidationProvider();
  return cachedProvider;
}

/**
 * Safe wrapper used by the router: never throws. An adapter exception is
 * collapsed to a clean `unable_to_verify` so a bad provider call can never
 * abort a payor create/update.
 */
export async function validatePayorSafely(
  input: PayorValidationInput,
  provider: PayorValidationProvider = getPayorValidationProvider(),
): Promise<PayorValidationResult> {
  try {
    return await provider.validate(input);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      status: 'unable_to_verify',
      provider: provider.name,
      details: {
        nameMatch: null,
        einMatch: null,
        registrySource: null,
        watchlistHits: [],
        reason: `Validation provider error: ${msg}`,
      },
    };
  }
}

/** Reset the cached provider — for tests + when the env changes at runtime. */
export function resetPayorValidationProviderCache(): void {
  cachedProvider = null;
}
